import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import * as Constants from '@emi/shared/constants';
import { SkuMetadataService, SkuPricesService } from '@emi/shared/data-access';
import {
    Category,
    ConfigureData,
    Country,
    PriceDto,
    Retailer,
    SearchOption,
    Sku,
    SkuPrice,
    SkuPriceDto,
    SkuPriceRequest,
    SkuSearchResult,
    UnitMeasure,
} from '@emi/shared/models';
import { CountryState, getCountryState, getSelectedConfigureData } from '@emi/shared/state/filters';
import {
    SkuSearchState,
    SortOptionsByPageState,
    getNextPageSkuSearchResults,
    getSkuSearchResults,
    selectSkuSearchState,
    setSortBy,
    selectSortByTLP,
    selectOrderByTLP
} from '@emi/shared/state/sku-search';
import { Store, select } from '@ngrx/store';
import * as _ from 'lodash';
import * as moment from 'moment';
import { Observable, Subject, Subscription, combineLatest, forkJoin } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import {
    TimeSeriesColumnLabels,
    TimeSeriesDataRow,
    TimeSeriesPrice,
    TimeSeriesSku,
    TimeSeriesSkuCollection,
    TimeSeriesTableData,
} from '../models';

export interface TimeSeriesIndices {
    start: number;
    end: number;
}

@Component({
    selector: 'via-sku-time-series-table',
    templateUrl: './sku-time-series-table.component.html',
    styleUrls: ['./sku-time-series-table.component.scss'],
})
export class SkuTimeSeriesTableComponent implements OnInit, OnDestroy {
    @Input()
    public isShowingAllPrices: boolean;

    @Input()
    public showRegularPrice: boolean;

    @Input()
    public selectedSortType = '';
    public selectedSortOrder = '';  
    public config: ConfigureData;
    public isLoading: boolean;


    public timeSeriesIndices: TimeSeriesIndices;
    public noOfColumns = Constants.TimeSeriesGrid.NUMBER_OF_COLUMNS;
    public indexIncrementSize = Constants.TimeSeriesGrid.INDEX_INCREMENT_SIZE;
    public skuData: Sku[] = [];
    public searchTerm: string;
    public searchOption: SearchOption;
    public skuInfo: TimeSeriesTableData | null;
    public includeSearchPhrase: boolean | undefined;
    public imageSasParams: string;
    private readonly firstChildFromArray: number = 0;
    private readonly categoryLevelOne: number = 1;
    private readonly skuData$: Observable<SkuSearchState> = this.store.select(selectSkuSearchState);
    private readonly countries$: Observable<CountryState> = this.store.select(getCountryState);
    private readonly config$: Observable<ConfigureData> = this.store.select(getSelectedConfigureData);
    private countries: Country[];
    private skuId: number[] = [];
    private emiItemId: number[] = [];
    private skuPrices: PriceDto[] = [];
    private timeSeriesColumnLabels: TimeSeriesColumnLabels[] = [];
    private pageIds: string[] = [];
    private currentSkuId: any[] = [];
    private currentEmiItemId: any[] = [];
    private skuIdGroup: any[] = [];
    private emiItemIdGroup: any[] = [];
    private currentIndex = 0;
    private dataSubscription$: Subscription;
    private periodOfTime: string[];
    private priceSubject$ = new Subject<SkuPriceRequest[]>();

    constructor(private store: Store, 
        private priceService: SkuPricesService, 
        private readonly sortOptionsByPageStore: Store<SortOptionsByPageState>,
        private skuMetadataService: SkuMetadataService) {}

    public ngOnInit(): void {
        this.getPriceResults();
        this.getSKUDetails();
        this.getSortOptionsFromStore();

        this.skuMetadataService.getImageSas().subscribe((sas) => {
            if (sas) {
                this.imageSasParams = '?' + sas;
            } else {
                this.imageSasParams = '';
            }
        });

        this.store.pipe(select(selectSkuSearchState)).subscribe((state) => {
            this.includeSearchPhrase = state.includeSearchPhrase;
        });

        this.getSearchSkuData();
     
    }

    public getSortOptionsFromStore(): void {
        this.sortOptionsByPageStore.pipe(select(selectSortByTLP),take(1)).subscribe((sortByTLP) => {
            this.selectedSortType = sortByTLP;
        });

        this.sortOptionsByPageStore.pipe(select(selectOrderByTLP), take(1)).subscribe((orderByTLP) => {
            this.selectedSortOrder = orderByTLP;
        });
    }

    public getSearchSkuData(){
        this.store.dispatch(setSortBy({ sortBy: this.selectedSortType }));
        this.store.dispatch(
            getSkuSearchResults({
                searchTerm: this.searchTerm,
                searchOption: this.searchOption,
                sortBy: this.selectedSortType,
                orderBy: this.selectedSortOrder,
                includeSearchPhrase: this.includeSearchPhrase,
            })
        );
    }

    public getTimeSeriesIndices(): void {
        const endIndex = this.skuInfo?.timeSeriesColumnLabels.length;
        if (endIndex) {
            if (endIndex < this.noOfColumns - 1) {
                this.updateTimeSeriesIndices(0, endIndex);
            } else {
                this.updateTimeSeriesIndices(endIndex - this.noOfColumns < 0 ? 0 : endIndex - this.noOfColumns, endIndex);
            }
        }
    }

    public updateTimeSeriesIndices(start: number, end: number): void {
        if (start < 0) {
            this.timeSeriesIndices = {
                start: 0,
                end: this.noOfColumns,
            };
        } else if (this.skuInfo && end > this.skuInfo.timeSeriesColumnLabels.length) {
            this.timeSeriesIndices = {
                start: this.skuInfo.timeSeriesColumnLabels.length - this.noOfColumns,
                end: this.skuInfo.timeSeriesColumnLabels.length,
            };
        } else {
            this.timeSeriesIndices = {
                start: start,
                end: end,
            };
        }
    }

    public prevButtonClicked(): void {
        this.updateTimeSeriesIndices(
            this.timeSeriesIndices.start - this.indexIncrementSize,
            this.timeSeriesIndices.end - this.indexIncrementSize
        );
    }

    public nextButtonClicked(): void {
        this.updateTimeSeriesIndices(
            this.timeSeriesIndices.start + this.indexIncrementSize,
            this.timeSeriesIndices.end + this.indexIncrementSize
        );
    }

    public disablePrevButton(): boolean {
        return this.timeSeriesIndices?.start === 0;
    }

    public disableNextButton(): boolean {
        return this.timeSeriesIndices.end === this.skuInfo?.timeSeriesColumnLabels.length;
    }

    public onScrollDown() {
        this.currentIndex++;
        if (
            (this.currentIndex < this.skuIdGroup.length && this.currentIndex < this.emiItemIdGroup.length) ||
            (this.pageIds && this.pageIds[this.pageIds.length - 1] !== '')
        ) {
            this.currentSkuId = this.skuIdGroup[this.currentIndex];
            this.currentEmiItemId = this.emiItemIdGroup[this.currentIndex];
            this.store.dispatch(getNextPageSkuSearchResults());
        }
    }

    public ngOnDestroy() {
        this.priceSubject$ && this.priceSubject$.unsubscribe();
        this.dataSubscription$ && this.dataSubscription$.unsubscribe();
    }

    private getSKUDetails() {
        this.currentIndex = 0;
        this.dataSubscription$ = combineLatest([this.skuData$, this.countries$, this.config$])
            .pipe(filter(([skuData, configData]) => Boolean(skuData && configData)))
            .subscribe(([skuData, countriesData, configData]) => {
                this.isLoading = skuData.isLoading;

                if (skuData.isLoading === false && this.checkDuplicates(skuData.skuResults, configData)) {
                    this.pageIds = skuData.pageIds;
                    this.skuId = [];
                    this.searchTerm = skuData.searchTerm;
                    this.searchOption = skuData.searchOption;
                    this.emiItemId = [];
                    this.skuData = skuData.skuResults;
                    if (this.config && JSON.stringify(this.config) !== JSON.stringify(configData)) {
                        this.skuPrices = [];
                        this.currentIndex = 0;
                        this.skuInfo = null;
                    }
                    this.config = configData;
                    this.skuData.forEach((sku) => {
                        this.skuId.push(sku.id);
                        this.emiItemId.push(sku.emiItemId);
                    });

                    this.skuIdGroup = [];
                    this.emiItemIdGroup = [];

                    while (this.skuId.length > 0) {
                        this.skuIdGroup.push(this.skuId.splice(0, 100));
                    }
                    while (this.emiItemId.length > 0) {
                        this.emiItemIdGroup.push(this.emiItemId.splice(0, 100));
                    }
                    this.currentSkuId = this.skuIdGroup[this.currentIndex];
                    this.currentEmiItemId = this.emiItemIdGroup[this.currentIndex];
                    this.countries = countriesData.countries;
                    this.getPriceDetails();
                }
            });
    }

    private mapSkuDetails() {
        // to sort industry code based on sku count----------------------------------
        const myMap = new Map();
        for (const obj of this.skuData) {
            if (myMap.has(obj.industryCode)) {
                myMap.set(obj.industryCode, myMap.get(obj.industryCode) + 1);
            } else {
                myMap.set(obj.industryCode, 1);
            }
        }

        const industryCode = Array.from(myMap)
            .sort((a, b) => b[1] - a[1])
            .map((x) => x[0]);
        //------------------------------------------------------------------------------
        const timeSeriesCollection: TimeSeriesSkuCollection[] = [];
        industryCode.forEach((industryCode) => {
            const filteredSkuData = this.skuData.filter((sku) => sku.industryCode === industryCode);
            timeSeriesCollection.push(
                new TimeSeriesSkuCollection(
                    filteredSkuData[0].category.name,
                    filteredSkuData.length,
                    this.getTimeSeriesSku(filteredSkuData)
                )
            );
        });

        this.skuInfo = {
            startDate: this.config.startDate ?? new Date(),
            endDate: this.config.endDate ?? new Date(),
            frequency: this.config.frequency,
            timeSeriesColumnLabels: this.timeSeriesColumnLabels,
            data: timeSeriesCollection,
        };
    }

    private getTimeSeriesSku(skuData: Sku[]): TimeSeriesSku[] {
        let skuTimeSeriesInfo: TimeSeriesSku[] = skuData.map((sku: Sku) => {
            return new TimeSeriesSku(
                new SkuSearchResult(
                    sku.emiItemId,
                    sku.name,
                    sku.emiImageUrl,
                    this.getCountryCode(sku.country.id),
                    sku.country.name,
                    sku.category.name,
                    this.getLevelOneCategoryDetails(sku)?.name,
                    this.getSubCategory(sku)?.name,
                    sku.supplier.name,
                    sku.brand.brandName,
                    sku.brand?.subBrands ? sku.brand?.subBrands[0].subBrandName : undefined,
                    this.getPackSize(sku)?.measure.toString(),
                    this.getUnitPerPack(sku)?.measure.toString() ?? undefined,
                    this.getTotalSKUSize(sku)?.toString()
                ),
                false,
                false,
                this.getTimeSeriesData(sku)
            );
        });
        // Filter SKUs based on itemId(emiItemId)
        skuTimeSeriesInfo = skuTimeSeriesInfo.filter(
            (sku, index) => skuTimeSeriesInfo.findIndex((element) => element.skuDetails.itemId === sku.skuDetails.itemId) === index
        );

        return skuTimeSeriesInfo;
    }

    private getCountryCode(countryId: number): string | null {
        return this.countries.find((country) => country.id === countryId)?.code ?? null;
    }

    private getTimeSeriesData(element: Sku): TimeSeriesDataRow[] {
        //Get all the retailers associated with an emiItemId
        const retailers = this.skuPrices.filter((sku) => sku.itemId === element.emiItemId)?.map((skuPrice) => skuPrice.retailers);
        const timeSeriesData: TimeSeriesDataRow[] = [];
        if (retailers.length > 0) {
            retailers?.forEach((retailer) => {
                timeSeriesData.push(
                    new TimeSeriesDataRow(
                        new Retailer(retailer[0].id, retailer[0].name),
                        this.getColumnPrice(element, retailer[0]),
                        this.calculatePriceGrowth(element, retailer[0])
                    )
                );
            });
        }
        // To display no data collected for SKUs when there is no retailer associated with it
        else {
            timeSeriesData.push(
                new TimeSeriesDataRow(
                    new Retailer(element.retailer.id, element.retailer.name),
                    this.getColumnPrice(element),
                    this.calculatePriceGrowth(element)
                )
            );
        }

        return timeSeriesData;
    }

    private getPriceDetails() {
        if (this.config?.startDate && this.config?.endDate && (this.currentEmiItemId || this.currentSkuId)) {
            this.calculateFrequencyPeriod();
            const skuPriceRequests: SkuPriceRequest[] = this.getSKUPriceRequests();
            this.priceSubject$.next(skuPriceRequests);
            this.isLoading = true;
        }
    }

    private getPriceResults() {
        this.priceSubject$
            .pipe(
                switchMap((skuPriceRequests: SkuPriceRequest[]) => {
                    const observables = skuPriceRequests.map((request) => this.priceService.getSkuPrices(request));
                    return forkJoin(observables);
                })
            )
            .subscribe((skuPriceDtos: SkuPriceDto[]) => {
                let results: PriceDto[] = [];

                skuPriceDtos.forEach((x) => (results = results.concat(x.results)));

                if (skuPriceDtos && skuPriceDtos.length > 0) {
                    this.skuPrices.push(...results);
                    const prices = [...this.skuPrices];
                    const result = this.skuPrices.filter(
                        (price, index) => index === prices.findIndex((other) => price.id === other.id && price.itemId === other.itemId)
                    );
                    this.skuPrices = [...result];
                    this.mapSkuDetails();
                    this.getTimeSeriesIndices();
                    this.isLoading = false;
                }
            });
    }

    private calculateFrequencyPeriod(): void {
        switch (this.config.frequency) {
            case 'Daily':
                this.calculateDays();
                break;
            case 'Weekly':
                this.calculateWeeks();
                break;
            case 'Monthly':
                this.calculateMonthly();
                break;
            case 'Quarterly':
                this.calculateQuarterly();
                break;
            case 'Yearly':
                this.calculateYears();
                break;
            default:
                break;
        }
    }

    private getSKUPriceRequests(): SkuPriceRequest[] {
        const skuIdPages =
            this.config.viewBy === Constants.Application.DEFAULT_VIEW_BY || this.config.viewBy === undefined
                ? this.emiItemIdGroup
                : this.skuIdGroup;

        const skuIds: any[] = [];

        skuIdPages.forEach((ids: any[]) => ids.forEach((id) => skuIds.push(id)));

        const requests: SkuPriceRequest[] = [];

        while (skuIds.length > 0) {
            const batch = skuIds.splice(0, Constants.GlobalVariables.MAX_SKUS_PER_PRICE_REQUEST);

            requests.push({
                currency: this.config.currency,
                startDate: moment(this.config.startDate).format(Constants.TimeSeriesGrid.DATE_FORMAT),
                endDate: moment(this.config.endDate).format(Constants.TimeSeriesGrid.DATE_FORMAT),
                includeRegularPrices: true,
                periodType: this.config.frequency,
                skuIds: batch.toString(),
                pricePer: this.config.pricePoint,
                viewBy: this.config.viewBy ?? Constants.Application.DEFAULT_VIEW_BY,
            });
        }

        return requests;
    }

    private getColumnPrice(element: Sku, retailer?: Retailer): TimeSeriesPrice[] {
        // Fetch the price for each retailer associated with the SKU
        const timeSeriesPrice: TimeSeriesPrice[] = [];
        if (retailer) {
            const skuPrices = this.fetchSKURetailerPrices(element, retailer);
            if (skuPrices) {
                this.periodOfTime.forEach((period) => {
                    const periodPrice = this.findPeriodPrice(skuPrices, period);
                    if (periodPrice) {
                        timeSeriesPrice.push(
                            new TimeSeriesPrice(
                                new Date(periodPrice.date),
                                periodPrice.price,
                                periodPrice.regularPrice,
                                periodPrice.isOffer,
                                periodPrice.isOutOfStock,
                                this.calculatePriceChange(skuPrices, period),
                                false
                            )
                        );
                    } else {
                        timeSeriesPrice.push(new TimeSeriesPrice(new Date(period), 0, 0, false, false, 0, true));
                    }
                });
            }
        } else {
            for (let i = 0; i < this.timeSeriesColumnLabels.length; i++)
                timeSeriesPrice.push(new TimeSeriesPrice(new Date(), 0, 0, false, false, 0, true));
        }

        return timeSeriesPrice;
    }

    private findPeriodPrice(skuPrices: SkuPrice[], period: string): SkuPrice | undefined {
        let skuPrice: SkuPrice | undefined;
        switch (this.config.frequency) {
            case 'Daily':
                skuPrice = skuPrices?.find(
                    (skuP) =>
                        moment(skuP.date).date() === moment(period).date() &&
                        moment(skuP.date).month() === moment(period).month() &&
                        moment(skuP.date).year() === moment(period).year()
                );
                break;
            case 'Weekly':
                skuPrice = skuPrices?.find(
                    (skuP) => moment(skuP.date).week() === moment(period).week() && moment(skuP.date).year() === moment(period).year()
                );
                break;
            case 'Monthly':
                skuPrice = skuPrices?.find(
                    (skuP) => moment(skuP.date).month() === moment(period).month() && moment(skuP.date).year() === moment(period).year()
                );
                break;
            case 'Quarterly':
                skuPrice = skuPrices?.find(
                    (skuP) => moment(skuP.date).quarter() === moment(period).quarter() && moment(skuP.date).year() === moment(period).year()
                );
                break;
            case 'Yearly':
                skuPrice = skuPrices?.find((skuP) => moment(skuP.date).year() === moment(period).year());
                break;
            default:
                break;
        }
        return skuPrice;
    }

    private calculatePriceChange(skuPrices: SkuPrice[], period: string) {
        const currentIndex = this.periodOfTime.findIndex((prd) => prd === period);
        if (currentIndex === 0 || skuPrices.length === 1) {
            return 0;
        }
        const previousDate = this.periodOfTime[currentIndex - 1];

        const currentPrice = this.findPeriodPrice(skuPrices, period);
        const previousPrice = this.findPeriodPrice(skuPrices, previousDate);

        if (!currentPrice?.price || !previousPrice?.price || currentPrice?.price === previousPrice?.price) {
            return 0;
        } else {
            return ((currentPrice.price - previousPrice.price) / previousPrice.price) * 100;
        }
    }

    private calculatePriceGrowth(element: Sku, retailer?: Retailer): number | undefined {
        // Fetch the price growth for each retailer associated with the SKU
        const growth = undefined;
        if (retailer) {
            const skuPrices = this.fetchSKURetailerPrices(element, retailer);
            if (skuPrices) {
                const lastPriceDate = this.periodOfTime[this.periodOfTime.length - 1];
                const firstPriceDate = this.periodOfTime[0];
                const firstPrice = this.findPeriodPrice(skuPrices, firstPriceDate);
                const lastPrice = this.findPeriodPrice(skuPrices, lastPriceDate);

                if (
                    lastPrice?.price === undefined ||
                    firstPrice?.price === undefined ||
                    lastPrice?.isOutOfStock ||
                    firstPrice?.isOutOfStock
                ) {
                    return growth;
                }
                if (+firstPrice.price === 0) {
                    return +lastPrice.price.toFixed(2);
                }
                if (lastPrice?.price === 0 && firstPrice.price === 0) {
                    return 0;
                } else {
                    return +(((lastPrice.price - firstPrice.price) / firstPrice.price) * 100).toFixed(2);
                }
            }
        }
        return growth;
    }

    private calculateDays(): void {
        this.timeSeriesColumnLabels = [];
        let tempDate = moment(this.config.startDate);
        this.periodOfTime = [];
        const noOfDays = moment(this.config.endDate).diff(moment(this.config.startDate), 'days');
        for (let i = 0; i <= noOfDays; i++) {
            const month = moment(tempDate).format('MMM');
            const startOfMonth = moment(tempDate).startOf('month').format(Constants.TimeSeriesGrid.DATE_FORMAT);
            const endOfYear = moment(tempDate).endOf('year').format(Constants.TimeSeriesGrid.DATE_FORMAT);
            const startOfYear = moment(tempDate).startOf('year').format(Constants.TimeSeriesGrid.DATE_FORMAT);
            const currentDate = tempDate.format(Constants.TimeSeriesGrid.DATE_FORMAT);
            const isFirstDayOfMonth = currentDate === startOfMonth;
            const isStartOrEndOfYear = currentDate === endOfYear || currentDate === startOfYear;
            const showYear = isStartOrEndOfYear ? `${moment(tempDate).format('MMM YYYY')}` : null;
            this.periodOfTime.push(moment(tempDate).format(Constants.TimeSeriesGrid.DATE_FORMAT));
            this.timeSeriesColumnLabels.push(new TimeSeriesColumnLabels([`${tempDate.date()}`], month, showYear, isFirstDayOfMonth));
            tempDate = moment(tempDate).add(1, 'days');
        }
    }

    private calculateMonthly(): void {
        this.timeSeriesColumnLabels = [];
        let tempDate = moment(this.config.startDate);
        this.periodOfTime = [];
        const noOfMonths = moment(this.config.endDate).diff(moment(this.config.startDate), 'months', true);
        for (let i = 1; i <= Math.ceil(noOfMonths); i++) {
            const month = moment(tempDate).format('MMM');
            const showYear =
                month === Constants.TimeSeriesGrid.JANUARY || month === Constants.TimeSeriesGrid.DECEMBER
                    ? `${moment(tempDate).year()}`
                    : null;
            this.periodOfTime.push(moment(tempDate).format(Constants.TimeSeriesGrid.DATE_FORMAT));
            this.timeSeriesColumnLabels.push(
                new TimeSeriesColumnLabels([`${tempDate.format('MMM')}`], month, showYear, month === Constants.TimeSeriesGrid.JANUARY)
            );
            tempDate = moment(tempDate).add(1, 'months');
        }
    }

    private calculateQuarterly(): void {
        this.timeSeriesColumnLabels = [];
        let tempDate = moment(this.config.startDate);
        this.periodOfTime = [];
        const noOfQtr = moment(this.config.endDate).diff(moment(this.config.startDate), 'quarter', true);
        for (let i = 1; i <= Math.ceil(noOfQtr); i++) {
            const month = moment(tempDate).format('MMM');
            const showYear =
                tempDate.quarter() === Constants.TimeSeriesGrid.QUARTER_FOUR || tempDate.quarter() === Constants.TimeSeriesGrid.QUARTER_ONE
                    ? `${moment(tempDate).year()}`
                    : null;
            this.periodOfTime.push(moment(tempDate).format(Constants.TimeSeriesGrid.DATE_FORMAT));
            this.timeSeriesColumnLabels.push(
                new TimeSeriesColumnLabels([`Q${tempDate.quarter()}`], month, showYear, month === Constants.TimeSeriesGrid.JANUARY)
            );
            tempDate = moment(tempDate).add(1, 'quarters');
        }
    }

    private calculateYears(): void {
        this.timeSeriesColumnLabels = [];
        let tempDate = moment(this.config.startDate);
        this.periodOfTime = [];
        const startDate = moment(this.config.startDate).year();
        const endDate = moment(this.config.endDate).year();
        const noOfYears = endDate - startDate + 1;
        for (let i = 1; i <= noOfYears; i++) {
            this.periodOfTime.push(moment(tempDate).format(Constants.TimeSeriesGrid.DATE_FORMAT));
            this.timeSeriesColumnLabels.push(new TimeSeriesColumnLabels([`${tempDate.year()}`], '', null, false));
            tempDate = moment(tempDate).add(1, 'years');
        }
    }
    private calculateWeeks(): TimeSeriesColumnLabels[] {
        // Calculate the no: of weeks ,if the end date is not Sunday add 1 to the no: of weeks
        this.timeSeriesColumnLabels = [];
        let tempDate = moment(this.config.startDate);
        const isEndDateDateSunday = moment(this.config.endDate).day() === 0;
        const isStartDateSunday = moment(this.config.startDate).day() === 0;
        const tempStartDate = !isStartDateSunday ? moment(this.config.startDate).clone().day(0) : moment(this.config.startDate);
        const tempEndDate = !isEndDateDateSunday ? moment(this.config.endDate).clone().day(0) : moment(this.config.endDate);
        let noOfWeeks = moment(tempEndDate).diff(moment(tempStartDate), 'weeks');
        noOfWeeks = isEndDateDateSunday ? noOfWeeks : noOfWeeks + 1;
        noOfWeeks = isStartDateSunday ? noOfWeeks + 1 : noOfWeeks;

        this.periodOfTime = [];
        let i = 0;
        // Logic if the startDate is a Sunday
        if (moment(this.config.startDate).day() === 0) {
            i = 1;
            const month = moment(tempDate).format('MMM');
            const weekNumber = moment(tempDate).week();
            const startOfMonthWeek = moment(tempDate).startOf('month').week();
            const endOfYearWeek = moment(tempDate).endOf('year').week();
            const isFirstWeekOfMonth = weekNumber === startOfMonthWeek;
            const showYear =
                tempDate.week() === endOfYearWeek || tempDate.week() === Constants.TimeSeriesGrid.WEEK_ONE
                    ? `${moment(tempDate).year()}`
                    : null;
            this.periodOfTime.push(moment(this.config.startDate).format(Constants.TimeSeriesGrid.DATE_FORMAT));
            this.timeSeriesColumnLabels.push(
                new TimeSeriesColumnLabels(
                    [`W${weekNumber}`],
                    month,
                    showYear,
                    isFirstWeekOfMonth || weekNumber === Constants.TimeSeriesGrid.WEEK_ONE
                )
            );
        }

        const current = moment(this.config.startDate).clone().day(0);
        for (i; i < noOfWeeks; i++) {
            const month = moment(tempDate).format('MMM');
            const weekNumber = moment(tempDate).week();
            const startOfMonthWeek = moment(tempDate).startOf('month').week();
            const endOfYearWeek = moment(tempDate).endOf('year').week();
            const showYear =
                tempDate.week() === endOfYearWeek || tempDate.week() === Constants.TimeSeriesGrid.WEEK_ONE
                    ? `${moment(tempDate).year()}`
                    : null;
            const isFirstWeekOfMonth = weekNumber === startOfMonthWeek;
            current.add(1, 'week');
            this.periodOfTime.push(current.format(Constants.TimeSeriesGrid.DATE_FORMAT));
            this.timeSeriesColumnLabels.push(
                new TimeSeriesColumnLabels(
                    [`W${weekNumber}`],
                    month,
                    showYear,
                    isFirstWeekOfMonth || weekNumber === Constants.TimeSeriesGrid.WEEK_ONE
                )
            );
            tempDate = moment(tempDate).add(1, 'weeks');
        }
        return this.timeSeriesColumnLabels;
    }

    private getLevelOneCategoryDetails(element: Sku): Category | null {
        let category: Category | null;
        if (element) {
            const categoryDetails = element.category?.childCategories?.filter((item) => item?.level === this.categoryLevelOne)?.[
                this.firstChildFromArray
            ];
            category = categoryDetails ?? null;
        } else {
            category = null;
        }
        return category;
    }

    private checkDuplicates(skuResults: Sku[], configureData: ConfigureData) {
        let tempSkuDataResults = _.cloneDeep(skuResults);
        let tempSkuData = _.cloneDeep(this.skuData);
        tempSkuDataResults = tempSkuDataResults.sort((a, b) => a.id - b.id);
        tempSkuData = tempSkuData.sort((a, b) => a.id - b.id);
        const tempConfig = _.cloneDeep(this.config);

        return !_.isEqual(tempSkuDataResults, tempSkuData) || !_.isEqual(tempConfig, configureData);
    }

    public getSubCategory(sku: Sku): Category | null {
        // Skips level 0 (industry) and level 1 (top category) and finds terminal node category.
        const childCategories = sku.category.childCategories;
        if (childCategories && childCategories[0].childCategories) {
            return this.getLowestLevelCategory(childCategories[0].childCategories[0]);
        }
        return null;
    }

    private getLowestLevelCategory(category: Category): Category {
        if (!category.childCategories) {
            return category;
        }
        return this.getLowestLevelCategory(category.childCategories[0]);
    }

    private getPackSize(element: Sku): UnitMeasure | null {
        let unitMeasure: UnitMeasure | null;
        if (element?.measures?.length) {
            const packSize = element.measures.filter((x) => x.measureName === Constants.TimeSeriesGrid.MEASURE_NAME_COUNT_PER_UNIT)?.[0];
            unitMeasure = packSize ?? null;
        } else {
            unitMeasure = null;
        }
        return unitMeasure;
    }

    private getUnitPerPack(element: Sku): UnitMeasure | null {
        let unitMeasure: UnitMeasure | null;
        if (element?.measures?.length) {
            const unitPerPack = element.measures.filter((x) => x.measureName === Constants.TimeSeriesGrid.MEASURE_NAME_QUANTITY)?.[0];
            unitMeasure = unitPerPack ?? null;
        } else {
            unitMeasure = null;
        }
        return unitMeasure;
    }

    private getTotalSKUSize(element: Sku): string | number | null {
        let totalSKUSize: string | number | null = null;
        if (element?.measures?.length) {
            const isDefaultMeasure = element.measures.filter((x) => x.isDefault && x.isDefault === 1)?.[0];
            if (isDefaultMeasure && isDefaultMeasure.measureName) {
                switch (isDefaultMeasure.measureName) {
                    case Constants.TimeSeriesGrid.MEASURE_NAME_QUANTITY:
                        totalSKUSize = element.totalSkuSize;
                        break;
                    case Constants.TimeSeriesGrid.MEASURE_NAME_VOLUME:
                    case Constants.TimeSeriesGrid.MEASURE_NAME_WEIGHT:
                        totalSKUSize = element.totalSkuSize + isDefaultMeasure.unitName;
                        break;
                    default:
                        break;
                }
            }
        } else {
            totalSKUSize = element.totalSkuSize;
        }
        return totalSKUSize;
    }

    private fetchSKURetailerPrices(element: Sku, retailer: Retailer): SkuPrice[] | undefined {
        const skuPrices: SkuPrice[] | undefined = this.skuPrices.find(
            (sp) => sp.itemId === element.emiItemId && sp.retailers.find((spRetailer) => spRetailer.id === retailer.id)
        )?.results;

        return skuPrices;
    }
}
